// Booking Pace dashboard page. This page spans several verified features, so
// inline FR refs are qualified: cancellation cards are booking-pace-cancellations
// (ADR-019); the Bookings-Per-Property / operational-count denominator is
// booking-pace-extra-metrics (ADR-020); the shared apartment-filter predicates are
// apartment-filters-everywhere; the layout editor (Edit/Save, desktop gate) is
// dashboard-layout-editor (ADR-022). Specs live under .verified/features/<slug>/.
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Card, CardContent } from "@/lib/shadcn/ui/card";
import { Input } from "@/lib/shadcn/ui/input";
import { Alert, AlertDescription } from "@/lib/shadcn/ui/alert";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/lib/shadcn/ui/accordion";
import { Loader2 } from "lucide-react";
import { SearchableSelect } from "@/lib/shadcn/components/searchable-select";
import { dateToString } from "@/utils/periods";
import { useAppDispatch, useAppSelector } from "@/store/store";
import {
    setEndDate,
    setPreviousEndDate,
    setPreviousStartDate,
    setPreviousYearEndDate,
    setPreviousYearStartDate,
    setStartDate,
    updateStats,
} from "@/store/bookingPaceSlice";
import { useGroupApartmentSync } from "@/hooks/useGroupApartmentSync";
import { useGetBookingPaceQuery } from "@/store/booking-management-api";
import { useAppData } from "@/hooks/useAppData";
import { useApartmentFilters } from "@/hooks/useApartmentFilters";
import { useAllApartments } from "@/store/slices/apartments";
import { operationalPropertyCount } from "@/utils/operationalProperties";
import ApartmentFilterBar from "@/lib/components/apartment-filter-bar";
import type { BookingPaceItem } from "@/models/responses";
import DashboardGrid from "@/lib/dashboard-grid/DashboardGrid";
import DashboardToolbar from "@/lib/dashboard-grid/DashboardToolbar";
import { useDashboardLayout } from "@/lib/dashboard-grid/useDashboardLayout";
import type { DashboardView, LayoutItem } from "@/lib/dashboard-grid/types";
import { apiWindowDay, buildBookingPaceTiles, inclusivePeriodDays, sectionIdForKey, type SectionStats } from "@/pages/dashboards/booking-pace-tiles";

const DASHBOARD_ID = "bookingPace";

/**
 * Desktop gate (FR-23, dashboard-layout-editor): the layout editor is offered only
 * at `lg`+ (1024px); below that Edit is disabled. Subscribes to matchMedia so a
 * resize across the breakpoint flips the gate live.
 */
function useIsDesktop(): boolean {
    const query = "(min-width: 1024px)";
    const [isDesktop, setIsDesktop] = useState<boolean>(() => {
        if (typeof window === "undefined" || !window.matchMedia) return true;
        return window.matchMedia(query).matches;
    });
    useEffect(() => {
        if (typeof window === "undefined" || !window.matchMedia) return;
        const mq = window.matchMedia(query);
        const onChange = () => setIsDesktop(mq.matches);
        // eslint-disable-next-line react-hooks/set-state-in-effect
        setIsDesktop(mq.matches);
        mq.addEventListener("change", onChange);
        return () => mq.removeEventListener("change", onChange);
    }, []);
    return isDesktop;
}

const BookingPacePage: React.FC = () => {
    const dispatch = useAppDispatch();
    const { selectedGroup, selectedApartment, groupOptions, apartmentOptions, handleGroupChange, handleApartmentChange } = useGroupApartmentSync();
    const { apartmentDetailsMap } = useAppData();
    const { predicates } = useApartmentFilters();
    const allApartments = useAllApartments();
    const isDesktop = useIsDesktop();

    /**
     * Apply the shared apartment-filter-bar predicates (apartment-filters-everywhere)
     * to a raw booking-pace payload. Records whose apartment isn't loaded (deleted
     * mid-period) are excluded under any active filter — matches the overview page.
     * Zero predicates returns the original array unchanged, so an un-filtered
     * portfolio gets the byte-identical payload it got before this wiring.
     */
    const applyFilters = (records: BookingPaceItem[] | undefined): BookingPaceItem[] | undefined => {
        if (!records) {
            return records;
        }
        if (predicates.length === 0) {
            return records;
        }
        return records.filter((record) => {
            const apartment = apartmentDetailsMap.get(record.apartmentId);
            if (!apartment) {
                return false;
            }
            return predicates.every((predicate) => predicate(apartment));
        });
    };

    const {
        startDate,
        endDate,
        previousStartDate,
        previousEndDate,
        previousYearStartDate,
        previousYearEndDate,
        bookingPaceStats,
        bookingPaceStatsPrevious,
        bookingPaceStatsPreviousYear,
    } = useAppSelector((state) => state.bookingPace);

    const {
        data: currentData,
        isLoading: isLoadingCurrent,
        error: errorCurrent,
    } = useGetBookingPaceQuery({
        startDate: apiWindowDay(startDate),
        endDate: apiWindowDay(endDate),
    });

    const {
        data: previousData,
        isLoading: isLoadingPrevious,
        error: errorPrevious,
    } = useGetBookingPaceQuery({
        startDate: apiWindowDay(previousStartDate),
        endDate: apiWindowDay(previousEndDate),
    });

    const {
        data: previousYearData,
        isLoading: isLoadingPreviousYear,
        error: errorPreviousYear,
    } = useGetBookingPaceQuery({
        startDate: apiWindowDay(previousYearStartDate),
        endDate: apiWindowDay(previousYearEndDate),
    });

    // Re-run when query data OR the active apartment-filter predicates change.
    // Filtering is client-side on the raw payload before dispatch so the accordion
    // aggregates only the rows the user asked for.
    useEffect(() => {
        if (currentData) {
            dispatch(
                updateStats({
                    data: applyFilters(currentData) ?? [],
                    type: "current",
                    apartmentId: selectedApartment,
                    groupId: selectedGroup,
                }),
            );
        }
        // applyFilters closes over predicates + apartmentDetailsMap; list those
        // directly rather than the function ref (new identity every render).
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentData, selectedApartment, selectedGroup, dispatch, predicates, apartmentDetailsMap]);

    useEffect(() => {
        if (previousData) {
            dispatch(
                updateStats({
                    data: applyFilters(previousData) ?? [],
                    type: "previous",
                    apartmentId: selectedApartment,
                    groupId: selectedGroup,
                }),
            );
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [previousData, selectedApartment, selectedGroup, dispatch, predicates, apartmentDetailsMap]);

    useEffect(() => {
        if (previousYearData) {
            dispatch(
                updateStats({
                    data: applyFilters(previousYearData) ?? [],
                    type: "previousYear",
                    apartmentId: selectedApartment,
                    groupId: selectedGroup,
                }),
            );
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [previousYearData, selectedApartment, selectedGroup, dispatch, predicates, apartmentDetailsMap]);

    const isLoading = isLoadingCurrent || isLoadingPrevious || isLoadingPreviousYear;
    const error = errorCurrent || errorPrevious || errorPreviousYear;
    // One day count per column, each from its OWN window: the comparison windows are
    // independently editable, so a previous-period numerator over the current window's
    // length would rescale the comparison by the difference in window lengths.
    const numberOfDays = inclusivePeriodDays(startDate, endDate);
    const numberOfDaysPrev = inclusivePeriodDays(previousStartDate, previousEndDate);
    const numberOfDaysPrevYear = inclusivePeriodDays(previousYearStartDate, previousYearEndDate);

    // Bookings Per Property denominator (booking-pace-extra-metrics): apartments
    // surviving the SAME selection applied to records — selected apartment/group +
    // shared apartment-filter predicates (FR-10) — counted per column against that
    // column's own window (FR-09), so YoY denominators reflect the portfolio size at
    // the time, not today's.
    const filteredApartments = allApartments
        .filter((apt) => {
            if (selectedApartment !== 0) {
                return apt.id === selectedApartment;
            }
            if (selectedGroup !== 0) {
                return apt.groupId === selectedGroup;
            }
            return true;
        })
        .filter((apt) => predicates.every((predicate) => predicate(apt)));

    const opCountCurrent = operationalPropertyCount(filteredApartments, startDate, endDate);
    const opCountPrev = operationalPropertyCount(filteredApartments, previousStartDate, previousEndDate);
    const opCountPrevYear = operationalPropertyCount(filteredApartments, previousYearStartDate, previousYearEndDate);

    const period = `${dateToString(startDate)} - ${dateToString(endDate)}`;

    // Sections in render order: "All Sources" forced first, then platforms by
    // grossIncome desc — mirrors the accordion's prior sort.
    const sectionsData: SectionStats[] = useMemo(() => {
        return Array.from(bookingPaceStats.entries())
            .sort(([, a], [, b]) => b.grossIncome - a.grossIncome)
            .map(([key]) => ({
                key,
                stats: bookingPaceStats.get(key),
                prev: bookingPaceStatsPrevious.get(key),
                prevYear: bookingPaceStatsPreviousYear.get(key),
            }));
    }, [bookingPaceStats, bookingPaceStatsPrevious, bookingPaceStatsPreviousYear]);

    // Build the generic {sections, registry} from the per-section stats. Recomputed
    // when any threaded input changes so tile closures read fresh stats. The Default
    // arrangement reproduces the current card order/grouping (FR-24,
    // dashboard-layout-editor).
    const { sections, registry } = useMemo(
        () =>
            buildBookingPaceTiles({
                sectionsData,
                numberOfDays,
                numberOfDaysPrev,
                numberOfDaysPrevYear,
                period,
                opCountCurrent,
                opCountPrev,
                opCountPrevYear,
                allStats: bookingPaceStats,
            }),
        [sectionsData, numberOfDays, numberOfDaysPrev, numberOfDaysPrevYear, period, opCountCurrent, opCountPrev, opCountPrevYear, bookingPaceStats],
    );

    const layout = useDashboardLayout(DASHBOARD_ID, sections, registry);
    const { working, editMode, setWorking } = layout;

    // ---- Working-view mutators (the page owns these; the grid components are
    // controlled). Each produces a fresh DashboardView so the hook's deep-compare
    // `modified` flag flips and read/edit renders stay in sync.

    const onLayoutChange = useCallback(
        (sectionId: string, items: LayoutItem[]) => {
            setWorking({
                ...working,
                sections: { ...working.sections, [sectionId]: items },
            });
        },
        [working, setWorking],
    );

    const onToggleHide = useCallback(
        (id: string) => {
            const isHidden = working.hidden.includes(id);
            const hidden = isHidden ? working.hidden.filter((h) => h !== id) : [...working.hidden, id];
            setWorking({ ...working, hidden });
        },
        [working, setWorking],
    );

    const onMove = useCallback(
        (id: string, dir: "prev" | "next") => {
            // Reorder within the tile's own section by swapping its rgl y with the
            // neighbour in render order. Sections are positioned single-column on the
            // page, so swapping y preserves the visual stack ordering.
            const sectionId = registry.find((t) => t.id === id)?.section;
            if (!sectionId) return;
            const items = [...(working.sections[sectionId] ?? [])];
            const ordered = [...items].sort((a, b) => (a.y ?? 0) - (b.y ?? 0) || (a.x ?? 0) - (b.x ?? 0));
            const idx = ordered.findIndex((it) => it.i === id);
            if (idx < 0) return;
            const swapWith = dir === "prev" ? idx - 1 : idx + 1;
            if (swapWith < 0 || swapWith >= ordered.length) return;
            const a = ordered[idx];
            const b = ordered[swapWith];
            const swapped = items.map((it) => {
                if (it.i === a.i) return { ...it, x: b.x, y: b.y };
                if (it.i === b.i) return { ...it, x: a.x, y: a.y };
                return it;
            });
            setWorking({
                ...working,
                sections: { ...working.sections, [sectionId]: swapped },
            });
        },
        [working, setWorking, registry],
    );

    // Scope each accordion section to a one-section grid so a drag is bounded to its
    // own section (no cross-section moves) while collapsibility is preserved.
    const sectionById = useMemo(() => new Map(sections.map((s) => [s.id, s])), [sections]);

    const renderSectionGrid = (sectionId: string): React.ReactNode => {
        const section = sectionById.get(sectionId);
        if (!section) return null;
        // A view scoped to this single section: reconcile inside the grid filters the
        // registry by section, so passing the whole working view + a single-section
        // list yields exactly this section's tiles.
        const scopedView: DashboardView = working;
        return (
            <DashboardGrid
                sections={[section]}
                registry={registry}
                view={scopedView}
                editMode={editMode}
                onLayoutChange={onLayoutChange}
                onToggleHide={onToggleHide}
                onMove={onMove}
                renderCtx={{}}
            />
        );
    };

    return (
        <div className="space-y-6" data-testid="booking-pace-page">
            {/* Apartment-level filters (shared across every Tier 1 page). */}
            <ApartmentFilterBar />

            <div className="flex justify-end gap-4">
                <div className="w-[200px]">
                    <SearchableSelect
                        variant="volt"
                        selectedValue={selectedGroup.toString()}
                        onChange={(value) => handleGroupChange(Number(value))}
                        placeholder="Select Group"
                        options={groupOptions}
                        selectedValuePrefix="Group"
                    />
                </div>

                <div className="w-auto">
                    <SearchableSelect
                        variant="volt"
                        selectedValue={selectedApartment.toString()}
                        onChange={(value) => handleApartmentChange(Number(value))}
                        placeholder="Select Apartment"
                        options={apartmentOptions}
                        selectedValuePrefix="Apartment"
                        width={"w-full"}
                    />
                </div>
            </div>

            <h2 className="text-2xl font-bold">Booking Pace</h2>

            {/* Layout editor toolbar (FR-23, dashboard-layout-editor: desktop-gated). */}
            <div className="flex flex-col gap-1">
                <DashboardToolbar
                    views={layout.views}
                    activeViewId={layout.activeViewId}
                    editMode={editMode}
                    modified={layout.modified}
                    onEnterEdit={layout.enterEdit}
                    onSave={layout.save}
                    onSaveAs={layout.saveAs}
                    onReset={layout.reset}
                    onDelete={layout.del}
                    onCancel={layout.cancel}
                    onSwitchView={layout.switchView}
                    canEdit={isDesktop}
                />
                {!isDesktop && (
                    <span className="text-sm text-muted-foreground" data-testid="edit-desktop-only-hint">
                        Editing available on a larger screen.
                    </span>
                )}
                {editMode && (
                    <p className="text-sm text-muted-foreground" data-testid="edit-instruction-line">
                        Drag the grip to reorder within a section · drag the right edge to resize · eye to hide. Changes aren&apos;t saved
                        until you Save.
                    </p>
                )}
            </div>

            <Card className="bg-white">
                <CardContent className="pt-6">
                    {/* Current Period */}
                    <div className="flex justify-between items-center">
                        <span className="text-sm text-muted-foreground">Bookings received between:</span>
                        <div className="flex items-center gap-2">
                            <Input
                                type="date"
                                value={apiWindowDay(startDate)}
                                onChange={(e) => dispatch(setStartDate(new Date(e.target.value)))}
                                max={apiWindowDay(endDate)}
                                className="w-auto"
                            />
                            <span>-</span>
                            <Input
                                type="date"
                                value={apiWindowDay(endDate)}
                                onChange={(e) => dispatch(setEndDate(new Date(e.target.value)))}
                                min={apiWindowDay(startDate)}
                                className="w-auto"
                            />
                        </div>
                    </div>

                    {/* Previous Period */}
                    <div className="flex justify-between items-center">
                        <span className="text-sm text-muted-foreground">vs Previous Period Dates:</span>
                        <div className="flex items-center gap-2">
                            <Input
                                type="date"
                                value={apiWindowDay(previousStartDate)}
                                onChange={(e) => dispatch(setPreviousStartDate(new Date(e.target.value)))}
                                max={apiWindowDay(previousEndDate)}
                                className="w-auto"
                            />
                            <span>-</span>
                            <Input
                                type="date"
                                value={apiWindowDay(previousEndDate)}
                                onChange={(e) => dispatch(setPreviousEndDate(new Date(e.target.value)))}
                                min={apiWindowDay(previousStartDate)}
                                className="w-auto"
                            />
                        </div>
                    </div>

                    {/* Previous Year */}
                    <div className="flex justify-between items-center">
                        <span className="text-sm text-muted-foreground">vs Previous Year Dates:</span>
                        <div className="flex items-center gap-2">
                            <Input
                                type="date"
                                value={apiWindowDay(previousYearStartDate)}
                                onChange={(e) => dispatch(setPreviousYearStartDate(new Date(e.target.value)))}
                                max={apiWindowDay(previousYearEndDate)}
                                className="w-auto"
                            />
                            <span>-</span>
                            <Input
                                type="date"
                                value={apiWindowDay(previousYearEndDate)}
                                onChange={(e) => dispatch(setPreviousYearEndDate(new Date(e.target.value)))}
                                min={apiWindowDay(previousYearStartDate)}
                                className="w-auto"
                            />
                        </div>
                    </div>
                </CardContent>
            </Card>

            {isLoading ? (
                <div role="status" aria-label="Loading booking pace data" className="flex justify-center items-center min-h-[200px]">
                    <Loader2 className="h-8 w-8 animate-spin" />
                    <span className="sr-only">Loading…</span>
                </div>
            ) : error ? (
                <Alert variant="destructive">
                    <AlertDescription>{error.toString()}</AlertDescription>
                </Alert>
            ) : bookingPaceStats.size === 0 ? (
                <Alert>
                    <AlertDescription>No data available for the selected date range. Please try a different range.</AlertDescription>
                </Alert>
            ) : (
                <Accordion type="multiple" defaultValue={["All Sources"]} className="space-y-4">
                    {sectionsData.map(({ key }) => {
                        const sectionId = sectionIdForKey(key);
                        return (
                            <AccordionItem key={key} value={key} className="border rounded-lg">
                                <AccordionTrigger className="hover:no-underline px-6">
                                    <span className="text-lg font-medium">{key}</span>
                                </AccordionTrigger>
                                <AccordionContent>
                                    <div className="px-6 pb-6">{renderSectionGrid(sectionId)}</div>
                                </AccordionContent>
                            </AccordionItem>
                        );
                    })}
                </Accordion>
            )}
        </div>
    );
};

export default BookingPacePage;
